/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-07-25 19:58:17
 *
 *******************************************************/
/// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal, Card, Tabs, Layout } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import _ from 'lodash';
import { reduxForm, getFormValues, reset } from 'redux-form';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import GeneralTab from './general-tab';
import AdvancedTab from './advanced-tab';
import Preview from './preview';
import {
  buildInitialFilterOptionValue,
  buildFilterOptionValue,
  validateSaveOption
} from 'src/helpers/filter-tree-option';
import multiLevelTagHelper from 'src/helpers/filter-tree-option/multi-level-tag-helper';
import collectionHelper from 'src/helpers/filter-tree-option/collection-helper';

import * as MultiLevelActions from 'actions/filter-tree/multi-level-actions';

type Props = WithI18nProps;

class OptionModal extends Component<Props, State> {
  state = {
    count: 0,
    selected: 0,
    activePreviewPanel: true,
    hasManualValuesChanged: false,
    hasMultiLevelManualValuesChanged: false
  };

  componentDidUpdate() {
    // Init multi level tag values at first time and save to store
    const {
      formFilterOptionValues,
      multiLevelTagManualValues = [],
      collectionMultiLevelManualValues = [],
      filterOption = {}
    } = this.props;
    const { filterType, displayType } = filterOption;
    const { optionType } = formFilterOptionValues;

    // Skip process if in page filter option list
    if (!optionType) return;

    // Init manual values for Multi-level tags
    if (
      (!filterType || filterType === 'multi_level_tag') &&
      multiLevelTagManualValues.length === 0 &&
      optionType === 'multi_level_tag'
    ) {
      this.buildInitialFilterOptionValue();
    }

    if (
      (!filterType || (filterType === 'collection' && displayType === 'multi_level_collections')) &&
      collectionMultiLevelManualValues.length === 0 &&
      optionType === 'collection'
    ) {
      this.buildInitialMultiLevelCollectionValues();
    }
  }

  componentWillReceiveProps(nextProps) {
    this.checkHasChangedMutiLevelTags(nextProps);
    this.checkHasChangeMultiLevelCollections(nextProps);
  }

  checkHasChangedMutiLevelTags = nextProps => {
    const initial = JSON.stringify(
      _.get(this.props.initialValues, 'multiLevelTagManualValues', [])
    );
    const next = JSON.stringify(
      multiLevelTagHelper.removeEmptyNoCompareProps(nextProps.multiLevelTagManualValues)
    );

    if (initial !== next) {
      this.setState({ hasManualValuesChanged: true });
    } else {
      this.setState({ hasManualValuesChanged: false });
    }
  };

  checkHasChangeMultiLevelCollections = nextProps => {
    const initial = JSON.stringify(
      _.get(this.props.initialValues, 'collectionMultiLevelManualValues', [])
    );
    const next = JSON.stringify(
      collectionHelper.removeEmptyNoCompareProps(nextProps.collectionMultiLevelManualValues)
    );

    if (initial !== next) {
      this.setState({ hasMultiLevelManualValuesChanged: true });
    } else {
      this.setState({ hasMultiLevelManualValuesChanged: false });
    }
  };

  render() {
    const { touch, active, handleSubmit, pristine, optionType, initialValues, index, i18n } =
      this.props;
    const {
      selected,
      activePreviewPanel,
      hasManualValuesChanged,
      hasMultiLevelManualValuesChanged
    } = this.state;

    const tabs = [
      {
        id: 'general',
        content: i18n.translate('AdminHTML.FilterTree.Item.optionModal_OptionModal_Tab1_Content'),
        panelID: 'general'
      },
      {
        id: 'advanced',
        content: i18n.translate('AdminHTML.FilterTree.Item.optionModal_OptionModal_Tab2_Content'),
        panelID: 'advanced'
      }
    ];

    const tabComponents = [
      <GeneralTab initialValues={initialValues} touch={touch} />,
      <AdvancedTab />
    ];
    return (
      <Modal
        open={active}
        onClose={this.handleChange}
        title={
          index !== -1
            ? i18n.translate('AdminHTML.FilterTree.Item.optionModal_OptionModal_Modal_Title1')
            : i18n.translate('AdminHTML.FilterTree.Item.optionModal_OptionModal_Modal_Title2')
        }
        large
        primaryAction={{
          content: i18n.translate('AdminHTML.Common.Action.save'),
          onAction: handleSubmit(this.handleSave.bind(this)),
          disabled:
            (pristine && !hasManualValuesChanged && !hasMultiLevelManualValuesChanged) ||
            _.isEmpty(optionType) ||
            optionType === 'no_metafield' ||
            optionType === 'no_range_slider'
        }}
        secondaryActions={[
          {
            content: i18n.translate('AdminHTML.Common.Action.cancel'),
            onAction: this.handleChange
          }
        ]}>
        <div className="BC-Modal__OptionFilter">
          <Layout>
            <Layout.Section>
              <div className="tabs">
                <Card
                  actions={
                    !activePreviewPanel
                      ? [
                          {
                            content: i18n.translate('AdminHTML.FilterTree.Common.preview'),
                            onAction: () => {
                              this.handleDismissPreviewModal();
                            }
                          }
                        ]
                      : []
                  }>
                  <form onSubmit={this.handleDefaultSubmit}>
                    <Tabs tabs={tabs} selected={selected} onSelect={this.handleTabChange}>
                      <Card.Section>{tabComponents[selected]}</Card.Section>
                    </Tabs>
                  </form>
                </Card>
              </div>
            </Layout.Section>

            <SwitchTransition>
              <CSSTransition key={activePreviewPanel} timeout={300} classNames="BC-Transition">
                {activePreviewPanel ? (
                  <Layout.Section secondary>
                    <Preview onDismiss={this.handleDismissPreviewModal} />
                  </Layout.Section>
                ) : (
                  <span></span>
                )}
              </CSSTransition>
            </SwitchTransition>
          </Layout>
        </div>
      </Modal>
    );
  }

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  // Handle change tab
  handleTabChange = (selectedTabIndex: number) => {
    this.setState({ selected: selectedTabIndex });
  };

  //Handle close modal
  handleChange = () => {
    const { handleChange, resetForm } = this.props;

    // Set the selected tab to 0
    this.setState({ selected: 0 });

    // Reset redux form
    resetForm();

    // Call handle change from parent
    handleChange();
  };

  // Handle click save button
  handleSave = data => {
    const { addFilterOption, updateFilterOption, index, filterOptions, i18n } = this.props;

    // Get from store and force update for Multi level tag manual values
    data.multiLevelTagManualValues = this.props.multiLevelTagManualValues;
    data.collectionMultiLevelManualValues = this.props.collectionMultiLevelManualValues;

    const optionIndex = index ? _.toNumber(index) : index;
    if (validateSaveOption(data, optionIndex, filterOptions, i18n)) {
      let filterOption = buildFilterOptionValue(data, optionIndex, filterOptions);

      if (optionIndex !== -1) {
        updateFilterOption(filterOption, optionIndex);
      } else {
        addFilterOption(filterOption);
      }

      this.handleChange();
    }
  };

  // Handle show/hide Preview panel
  handleDismissPreviewModal = () => {
    this.setState(({ activePreviewPanel }) => {
      return { activePreviewPanel: !activePreviewPanel };
    });
  };

  // Build and save multi level tags in store
  buildInitialFilterOptionValue = () => {
    const { filterOption } = this.props;
    const initialValues = buildInitialFilterOptionValue(filterOption);

    // Get multi-level tags option values
    const multiLevelTagValues = _.pick(initialValues, ['multiLevelTagManualValues']);
    this.props.initialMultiLevelTags(multiLevelTagValues);
  };

  buildInitialMultiLevelCollectionValues = () => {
    const { filterOption } = this.props;
    const initialValues = buildInitialFilterOptionValue(filterOption);

    // Get multi-level tags option values
    const multiLevelColectionValues = _.pick(initialValues, ['collectionMultiLevelManualValues']);

    this.props.initialMultiLevelCollections(multiLevelColectionValues);
  };
}

const OptionModalForm = reduxForm({
  form: 'OptionModalForm', // a unique identifier for this form
  enableReinitialize: true
})(OptionModal);

const mapStateToProps = (state, props) => {
  const formFilterOptionValues = getFormValues('OptionModalForm')(state);
  const formFilterTreeValues = getFormValues('FilterTreeForm')(state);
  const { filterOption } = props;

  // Get multi level tag from store
  const multiLevelTags = _.get(state.multiLevelFilterOption, 'multiLevelTags', {});

  // Get multi level tag from store
  const multiLevelCollections = _.get(state.multiLevelFilterOption, 'multiLevelCollections', {});

  return {
    optionType: _.get(formFilterOptionValues, 'optionType', ''),
    filterOptions: _.get(formFilterTreeValues, 'filterOptions', []),
    initialValues: buildInitialFilterOptionValue(filterOption),
    multiLevelTagManualValues: _.get(multiLevelTags, 'multiLevelTagManualValues', []),
    collectionMultiLevelManualValues: _.get(
      multiLevelCollections,
      'collectionMultiLevelManualValues',
      []
    ),
    formFilterOptionValues
  };
};

const mapDispatchToProps = dispatch => ({
  resetForm: () => dispatch(reset('OptionModalForm')),
  initialMultiLevelTags: filterOption =>
    dispatch(MultiLevelActions.initialMultiLevelTags(filterOption)),
  initialMultiLevelCollections: filterOption =>
    dispatch(MultiLevelActions.initialMultiLevelCollections(filterOption)),
  resetManualValuesMultiLevelTags: () =>
    dispatch(MultiLevelActions.updateManualValuesMultiLevelTags([])),
  resetManualValuesMultiLevelCollections: () =>
    dispatch(MultiLevelActions.updateManualValuesMultiLevelCollections([]))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(OptionModalForm));
